import { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

// Notification types that mirror WhatsApp messages
const NOTIFICATION_TYPES = {
  BOOKING_CONFIRMED: 'booking_confirmed',
  DRIVER_ASSIGNED: 'driver_assigned',
  DRIVER_ON_THE_WAY: 'driver_on_the_way',
  TRACKING_AVAILABLE: 'tracking_available',
  PAYMENT_RECEIVED: 'payment_received',
  TRIP_COMPLETED: 'trip_completed',
  MESSAGE: 'message'
};

const STORAGE_KEY = 'ygo_notifications';
const MAX_NOTIFICATIONS = 50;

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [lastBookingId, setLastBookingId] = useState(null);

  // Load notifications from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setNotifications(parsed);
      }
    } catch (e) {
      console.error('Failed to load notifications:', e);
    }
  }, []);

  // Save notifications to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
    } catch (e) {
      console.error('Failed to save notifications:', e);
    }
  }, [notifications]);

  // Add notification to history
  const addToHistory = useCallback((type, title, body, data = {}) => {
    const notification = {
      id: `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      title,
      body,
      data,
      timestamp: new Date().toISOString(),
      read: false
    };

    setNotifications(prev => {
      const updated = [notification, ...prev].slice(0, MAX_NOTIFICATIONS);
      return updated;
    });

    return notification;
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(n => ({ ...n, read: true }))
    );
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Show notification with appropriate styling and add to history
  const showNotification = useCallback((type, data) => {
    let title = '';
    let body = '';

    switch (type) {
      case NOTIFICATION_TYPES.BOOKING_CONFIRMED:
        title = 'Booking Confirmed!';
        body = `Your booking ${data.booking_id} has been confirmed.`;
        toast.success(title, {
          description: body,
          duration: 6000,
          action: data.trackingUrl ? {
            label: 'Track',
            onClick: () => window.location.href = data.trackingUrl
          } : undefined
        });
        break;

      case NOTIFICATION_TYPES.DRIVER_ASSIGNED:
        title = 'Driver Assigned!';
        body = `${data.driver_name || 'Your driver'} has been assigned to your booking.`;
        toast.info(title, {
          description: body,
          duration: 5000,
          icon: '🚗'
        });
        break;

      case NOTIFICATION_TYPES.DRIVER_ON_THE_WAY:
        title = 'Driver On The Way!';
        body = `${data.driver_name || 'Your driver'} is heading to pick you up.`;
        toast.info(title, {
          description: body,
          duration: 8000,
          icon: '🚐',
          action: data.trackingUrl ? {
            label: 'Track Live',
            onClick: () => window.location.href = data.trackingUrl
          } : undefined
        });
        break;

      case NOTIFICATION_TYPES.TRACKING_AVAILABLE:
        title = 'Live Tracking Available!';
        body = 'You can now track your driver in real-time.';
        toast.info(title, {
          description: body,
          duration: 6000,
          icon: '📍',
          action: data.trackingUrl ? {
            label: 'Track',
            onClick: () => window.location.href = data.trackingUrl
          } : undefined
        });
        break;

      case NOTIFICATION_TYPES.PAYMENT_RECEIVED:
        title = 'Payment Received!';
        body = `Payment of €${data.amount?.toFixed(2) || '0.00'} has been confirmed.`;
        toast.success(title, {
          description: body,
          duration: 5000,
          icon: '✅'
        });
        break;

      case NOTIFICATION_TYPES.TRIP_COMPLETED:
        title = 'Trip Completed!';
        body = 'Thank you for using Y GO. Have a great day!';
        toast.success(title, {
          description: body,
          duration: 5000,
          icon: '🎉'
        });
        break;

      case NOTIFICATION_TYPES.MESSAGE:
        title = `New message from ${data.sender_name || 'Driver'}`;
        body = data.content || '';
        toast.info(title, {
          description: body.length > 50 ? body.substring(0, 50) + '...' : body,
          duration: 4000,
          icon: '💬'
        });
        break;

      default:
        title = data.title || 'Notification';
        body = data.message || '';
        toast.info(title, {
          description: body,
          duration: 4000
        });
    }

    // Add to history
    addToHistory(type, title, body, data);
  }, [addToHistory]);

  // Notify booking confirmed
  const notifyBookingConfirmed = useCallback((bookingData) => {
    showNotification(NOTIFICATION_TYPES.BOOKING_CONFIRMED, {
      booking_id: bookingData.booking_id,
      trackingUrl: bookingData.tracking_token 
        ? `/track/${bookingData.tracking_token}`
        : `/track/${bookingData.booking_id}`
    });
    setLastBookingId(bookingData.booking_id);
  }, [showNotification]);

  // Notify driver assigned
  const notifyDriverAssigned = useCallback((data) => {
    showNotification(NOTIFICATION_TYPES.DRIVER_ASSIGNED, {
      booking_id: data.booking_id,
      driver_name: data.driver_name
    });
  }, [showNotification]);

  // Notify driver on the way
  const notifyDriverOnTheWay = useCallback((data) => {
    showNotification(NOTIFICATION_TYPES.DRIVER_ON_THE_WAY, {
      booking_id: data.booking_id,
      driver_name: data.driver_name,
      trackingUrl: data.tracking_url || `/track/${data.tracking_token || data.booking_id}`
    });
  }, [showNotification]);

  // Notify tracking available
  const notifyTrackingAvailable = useCallback((data) => {
    showNotification(NOTIFICATION_TYPES.TRACKING_AVAILABLE, {
      booking_id: data.booking_id,
      trackingUrl: data.tracking_url || `/track/${data.tracking_token || data.booking_id}`
    });
  }, [showNotification]);

  // Notify payment received
  const notifyPaymentReceived = useCallback((data) => {
    showNotification(NOTIFICATION_TYPES.PAYMENT_RECEIVED, {
      booking_id: data.booking_id,
      amount: data.amount
    });
  }, [showNotification]);

  // Notify trip completed
  const notifyTripCompleted = useCallback((data) => {
    showNotification(NOTIFICATION_TYPES.TRIP_COMPLETED, {
      booking_id: data.booking_id
    });
  }, [showNotification]);

  // Notify new message
  const notifyNewMessage = useCallback((data) => {
    showNotification(NOTIFICATION_TYPES.MESSAGE, {
      sender_name: data.sender_name,
      content: data.content
    });
  }, [showNotification]);

  const value = {
    notifications,
    showNotification,
    notifyBookingConfirmed,
    notifyDriverAssigned,
    notifyDriverOnTheWay,
    notifyTrackingAvailable,
    notifyPaymentReceived,
    notifyTripCompleted,
    notifyNewMessage,
    markAllAsRead,
    clearAllNotifications,
    lastBookingId,
    NOTIFICATION_TYPES
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export { NOTIFICATION_TYPES };
